import { Component } from '@theme/component';

/**
 * Kokochao V15 Alpha 1.9.2 Header Final Freeze.
 * Progressive migration of Dawn sticky/menu behavior to the Horizon Component + ThemeDrawer runtime.
 */
class KcH15Header extends Component {
  #abortController = new AbortController();
  #lastScrollY = 0;
  #headerBoundsBottom = 0;
  #stickyType = 'on-scroll-up';
  #resizeObserver = null;
  #mobileTrigger = null;

  connectedCallback() {
    super.connectedCallback();
    this.#stickyType = this.dataset.stickyType || 'on-scroll-up';
    this.#lastScrollY = window.scrollY || 0;
    this.#measure();
    this.#bindEvents();
    this.#observeSize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
  }

  #bindEvents() {
    const { signal } = this.#abortController;
    window.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
    window.addEventListener('resize', () => this.#measure(), { passive: true, signal });

    this.querySelectorAll('[data-kc-h15-nav-details]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.#closeDesktopMenus(details);
      }, { signal });
    });

    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.#closeDesktopMenus();
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openDetails = this.querySelector('[data-kc-h15-nav-details][open]');
      if (openDetails) {
        const summary = openDetails.querySelector(':scope > summary');
        openDetails.open = false;
        summary?.focus();
      }
    }, { signal });

    document.addEventListener('toggle', (event) => {
      if (!(event.target instanceof HTMLDialogElement) || !event.target.open) return;
      this.#closeDesktopMenus();
    }, { capture: true, signal });

    this.addEventListener('theme-drawer:open', (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.matches('.kc-h15-nav-drawer')) return;
      this.classList.add('kc-h15-nav-is-open');
      this.querySelectorAll('.kc-h15-nav-trigger').forEach((trigger) => {
        trigger.setAttribute('aria-hidden', 'true');
        trigger.setAttribute('tabindex', '-1');
      });
      this.#closeDesktopMenus();
    }, { signal });

    this.addEventListener('theme-drawer:close', (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.matches('.kc-h15-nav-drawer')) return;
      this.classList.remove('kc-h15-nav-is-open');
      this.querySelectorAll('.kc-h15-nav-trigger').forEach((trigger) => {
        trigger.removeAttribute('aria-hidden');
        trigger.removeAttribute('tabindex');
      });
    }, { signal });

    this.querySelectorAll('[data-kc-h15-mobile-open]').forEach((button) => {
      button.addEventListener('click', () => this.#openMobilePanel(button), { signal });
    });

    this.querySelectorAll('[data-kc-h15-mobile-back]').forEach((button) => {
      button.addEventListener('click', () => this.#resetMobilePanels(true), { signal });
    });

    this.querySelectorAll('[data-kc-h15-nav-dialog]').forEach((dialog) => {
      dialog.addEventListener('close', () => this.#resetMobilePanels(false), { signal });
    });
  }

  #observeSize() {
    if (!('ResizeObserver' in window)) return;
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this);
  }

  #measure() {
    const section = this.closest('.section-header');
    const rect = section?.getBoundingClientRect() || this.getBoundingClientRect();
    this.#headerBoundsBottom = (window.scrollY || 0) + rect.bottom;
    document.documentElement.style.setProperty('--header-height', `${Math.round(rect.height)}px`);
  }

  #onScroll() {
    const section = this.closest('.section-header');
    if (!section || this.#stickyType === 'none') return;

    const currentY = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
    const movingDown = currentY > this.#lastScrollY + 2;
    const movingUp = currentY < this.#lastScrollY - 2;
    const pastHeader = currentY > Math.max(24, this.#headerBoundsBottom - 12);

    if (this.#stickyType === 'always' || this.#stickyType === 'reduce-logo-size') {
      section.classList.add('shopify-section-header-sticky');
      section.classList.toggle('scrolled-past-header', pastHeader);
      section.classList.remove('shopify-section-header-hidden');
    } else if (!pastHeader) {
      section.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'scrolled-past-header');
    } else if (movingDown) {
      section.classList.add('shopify-section-header-sticky', 'scrolled-past-header', 'shopify-section-header-hidden');
      this.#closeDesktopMenus();
    } else if (movingUp) {
      section.classList.add('shopify-section-header-sticky', 'scrolled-past-header');
      section.classList.remove('shopify-section-header-hidden');
    }

    this.#lastScrollY = currentY;
  }

  #openMobilePanel(button) {
    const id = button.dataset.kcH15MobileOpen;
    if (!id) return;
    const panel = this.querySelector(`[data-kc-h15-mobile-panel="${CSS.escape(id)}"]`);
    const root = this.querySelector('[data-kc-h15-mobile-root]');
    if (!panel || !root) return;

    this.#mobileTrigger = button;
    root.hidden = true;
    this.querySelectorAll('[data-kc-h15-mobile-panel]').forEach((candidate) => {
      candidate.hidden = candidate !== panel;
    });
    panel.hidden = false;
    panel.querySelector('h3')?.focus({ preventScroll: true });
    panel.scrollIntoView({ block: 'start' });
  }

  #resetMobilePanels(restoreFocus = false) {
    const root = this.querySelector('[data-kc-h15-mobile-root]');
    if (root) root.hidden = false;
    this.querySelectorAll('[data-kc-h15-mobile-panel]').forEach((panel) => { panel.hidden = true; });
    if (restoreFocus) this.#mobileTrigger?.focus({ preventScroll: true });
    this.#mobileTrigger = null;
  }

  #closeDesktopMenus(except = null) {
    this.querySelectorAll('[data-kc-h15-nav-details][open]').forEach((details) => {
      if (details !== except) details.open = false;
    });
  }
}

if (!customElements.get('kc-h15-header')) {
  customElements.define('kc-h15-header', KcH15Header);
}

// Dawn 16 / new Customer Accounts positioning retained during the migration.
document.addEventListener('open', (event) => {
  if (!(event.target instanceof HTMLElement) || !event.target.matches('shopify-account')) return;
  const bottom = Math.max(0, Math.round(event.target.getBoundingClientRect().bottom));
  event.target.style.setProperty('--account-dialog-top', `${bottom}px`);
}, true);
